import { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ActivityCard from "../cards/ActivityCard";
import { excludeStaff } from "../../utils/timetableUtils";

function sortGroups(groups = []) {
  return [...groups].sort((a, b) => {
    const aName = String(a.activityName || "").trim();
    const bName = String(b.activityName || "").trim();
    return aName.localeCompare(bName);
  });
}

function filterAdults(people = [], showAdults = false) {
  if (showAdults) return people;
  return people.filter((person) => person.personType !== "ADULT");
}

function isArrivingToday(person) {
  return person.arrivingToday === 1 || person.arrivingToday === true;
}

function isLeavingToday(person) {
  return person.departingToday === 1 || person.departingToday === true;
}

function isArrivingOrLeavingToday(person) {
  return isArrivingToday(person) || isLeavingToday(person);
}

function buildLaneTitle(slot, titleOverride) {
  if (titleOverride) return titleOverride;

  switch (slot) {
    case "AFTERNOON":
      return "Afternoon";
    case "EVENING":
      return "Evening";
    case "DAY":
      return "Day";
    default:
      return "Activities";
  }
}

export default function ActivityLane({
  timetable = { unassigned: [], groups: [] },
  slot = "AFTERNOON",
  selectedDate,
  selectedCampusId,
  onOpenActivities,
  onOpenTransport,
  title,
  tv = false,
  tvColumns = 3,
  tvCardRange = null,
}) {
  const [showAdults, setShowAdults] = useState(true);
  const [showDayEdgeCases, setShowDayEdgeCases] = useState(true);
  const [showEveningLeavers, setShowEveningLeavers] = useState(true);

  // A person leading a group is also returned in `unassigned` by the API — drop them so
  // they don't appear in both places (they're clearly assigned: they're the leader).
  const leaderKeys = useMemo(() => {
    const keys = new Set();
    (timetable.groups || []).forEach((g) => {
      if (g?.leaderAdultId != null) keys.add(`ADULT:${g.leaderAdultId}`);
      if (g?.leaderStaffId != null) keys.add(`STAFF:${g.leaderStaffId}`);
    });
    return keys;
  }, [timetable.groups]);

  const visibleUnassigned = useMemo(() => {
    let people = (timetable.unassigned || []).filter(
      (person) => !leaderKeys.has(`${person.personType}:${person.personId}`)
    );
    people = filterAdults(people, showAdults);
    if (tv) people = excludeStaff(people); // TV view drops our own staff

    if (slot === "DAY" && !showDayEdgeCases) {
      people = people.filter((person) => !isArrivingOrLeavingToday(person));
    }

    if (slot === "EVENING" && !showEveningLeavers) {
      people = people.filter((person) => !isLeavingToday(person));
    }

    return people;
  }, [
    timetable.unassigned,
    leaderKeys,
    showAdults,
    slot,
    showDayEdgeCases,
    showEveningLeavers,
    tv,
  ]);

  const visibleGroups = useMemo(() => {
    return sortGroups(timetable.groups || []).map((group) => {
      let members = filterAdults(group.members || [], showAdults);
      if (tv) members = excludeStaff(members);
      return { ...group, members };
    });
  }, [timetable.groups, showAdults, tv]);

  const laneTitle = buildLaneTitle(slot, title);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">{laneTitle}</Typography>

        {tv ? null : (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={showAdults}
                onChange={(e) => setShowAdults(e.target.checked)}
                size="small"
              />
            }
            label="Include adults"
            sx={{ mr: 1 }}
          />

          {slot === "DAY" ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showDayEdgeCases}
                  onChange={(e) => setShowDayEdgeCases(e.target.checked)}
                  size="small"
                />
              }
              label="Include arrivers/leavers"
              sx={{ mr: 1 }}
            />
          ) : null}

          {slot === "EVENING" ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showEveningLeavers}
                  onChange={(e) => setShowEveningLeavers(e.target.checked)}
                  size="small"
                />
              }
              label="Include leavers"
              sx={{ mr: 1 }}
            />
          ) : null}

          {onOpenActivities ? (
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => onOpenActivities(selectedDate, selectedCampusId, slot)}
            >
              Open Activities →
            </Typography>
          ) : null}

          {onOpenTransport ? (
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => onOpenTransport(selectedDate, selectedCampusId)}
            >
              Open Transport →
            </Typography>
          ) : null}
        </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {(() => {
          // Flatten to one ordered card list (Unassigned first, then groups) so TV
          // view can render just a slice of it when a busy lane spans several screens.
          const hasUnassigned = visibleUnassigned.length > 0;
          const cards = [];
          if (hasUnassigned) {
            cards.push(
              <ActivityCard
                key="unassigned"
                activityName="Unassigned"
                residents={visibleUnassigned}
                isUnassigned
                colorIndex={0}
                slot={slot}
                tv={tv}
                tvColumns={tvColumns}
              />
            );
          }
          visibleGroups.forEach((group, index) => {
            cards.push(
              <ActivityCard
                key={group.groupId ?? `${group.activityName}-${index}`}
                groupId={group.groupId}
                activityName={group.activityName}
                leaderName={group.leaderName}
                leaderStaffId={group.leaderStaffId}
                leaderAdultId={group.leaderAdultId}
                residents={group.members || []}
                colorIndex={index + (hasUnassigned ? 1 : 0)}
                slot={slot}
                tv={tv}
                tvColumns={tvColumns}
              />
            );
          });
          return tvCardRange ? cards.slice(tvCardRange[0], tvCardRange[1]) : cards;
        })()}
      </Stack>
    </Box>
  );
}